import React, { useCallback } from 'react';
import { GeoJSON } from 'react-leaflet';
import type { Layer, PathOptions } from 'leaflet';
import {
  CADASTRAL_BOUNDARY_STYLE,
  getForestStandStyle,
} from '@/components/ui/FmStandMapSemantics';
import { resolvePathColors } from '@/components/Map/resolveLeafletColor';
import { useMapGround } from '@/components/Map/mapGround';
import { markMapLayerElement } from '@/components/ui/mapLayerDomMarkers';

interface ForestStandGeoJsonLayerProps {
  data: GeoJSON.FeatureCollection;
  layerKey?: string;
  pane?: string;
  onEachFeature?: (feature: GeoJSON.Feature, layer: Layer) => void;
  styleOverride?: (feature?: GeoJSON.Feature) => PathOptions;
}

interface PropertyBoundaryLayerProps {
  data: GeoJSON.GeoJsonObject;
  layerKey?: string;
  pane?: string;
  testId?: string;
  onEachFeature?: (feature: GeoJSON.Feature, layer: Layer) => void;
}

export const ForestStandGeoJsonLayer: React.FC<ForestStandGeoJsonLayerProps> = ({
  data,
  layerKey,
  pane,
  onEachFeature,
  styleOverride,
}) => {
  // Resolve var() tokens to computed colors so Leaflet's SVG fill/stroke
  // attributes render distinct per-feature colors. They resolve on the map
  // container, and the layer redraws when the basemap's ground changes, so a
  // ground-dependent role matches the basemap under it.
  const { ground, container } = useMapGround();
  const style = useCallback(
    (feature?: GeoJSON.Feature) =>
      resolvePathColors(styleOverride?.(feature) ?? getForestStandStyle(feature ?? null), container),
    [styleOverride, container],
  );
  const handleFeature = useCallback(
    (feature: GeoJSON.Feature, layer: Layer) => {
      onEachFeature?.(feature, layer);
    },
    [onEachFeature],
  );

  return (
    <GeoJSON
      key={`${layerKey ?? `forest-stands-${data.features?.length ?? 0}`}:${ground}`}
      data={data}
      style={style}
      pane={pane}
      onEachFeature={handleFeature}
    />
  );
};

export const PropertyBoundaryLayer: React.FC<PropertyBoundaryLayerProps> = ({
  data,
  layerKey,
  pane,
  testId,
  onEachFeature,
}) => {
  const { container } = useMapGround();
  const markBoundaryFeature = useCallback(
    (feature: GeoJSON.Feature, layer: Layer) => {
      markMapLayerElement(layer, { layerId: 'property-boundary', testId });
      onEachFeature?.(feature, layer);
    },
    [onEachFeature, testId],
  );

  return (
    <GeoJSON
      key={layerKey ?? 'property-boundary'}
      data={data}
      // Style as a function so var() tokens resolve at draw time (DOM/theme ready),
      // not at module-import time.
      style={() => resolvePathColors(CADASTRAL_BOUNDARY_STYLE, container)}
      pane={pane}
      onEachFeature={markBoundaryFeature}
    />
  );
};
